/**
 * Image subtitles (SUP/PGS, VobSub) on the tool pages: telling them apart, sending them to the
 * OCR pages, and loading the OCR module only when one arrives.
 *
 * In the page bundle and kept small on purpose: everything heavy (tesseract.js, libbitsub, the
 * English model) is in `/static/build/ocr/ocr.js` and `/static/vendor/`, which this module
 * imports by URL on first use. Nothing here fetches anything on a page load.
 *
 * ── THE ENTRY POINT FOR AN IMAGE TRACK INSIDE A VIDEO ─────────────────────
 *
 * The video path (SEO_BRIEF_04 item 7) finds PGS and VobSub tracks inside MKV and MP4 files and
 * writes them out as the same files a user would drop: a `.sup`, or an `.idx` and a `.sub`. It
 * hands them over with
 *
 *     sendImageSubtitlesToOcr(source, name)
 *
 * which stores them in IndexedDB and opens the right OCR page (/sup-to-srt for PGS,
 * /idx-sub-to-srt for VobSub), where `takeHandedOver` picks them up and recognition starts as
 * though the files had been dropped there. `source` is `ImageSubtitleSource`
 * (src/ocr/types.ts): `{kind: 'pgs', sup}` or `{kind: 'vobsub', idx, sub}`.
 *
 * The two halves meet at `ocrHandoverHandler` (the bottom of this file): every tool page
 * registers it at boot (pages/tool.ts), so picking an image track in the video path's track
 * list sends it to OCR, or recognises it in place on an OCR page.
 */

import {
  type DroppedHead,
  type ImageDrop,
  imageDrop,
  mightBeImageSubtitle,
} from '../../../../../src/ocr/pair.ts';
import type { ContainerTrackInfo } from '../../../../../src/core/protocol.ts';
import type { ImageSubtitleSource } from '../../../../../src/ocr/types.ts';

export type { ImageDrop, ImageSubtitleSource };

/** The OCR pages, by what they read first. Both run either format. */
export const OCR_PAGES = { pgs: '/sup-to-srt', vobsub: '/idx-sub-to-srt' } as const;

/** True on a page whose row reads image subtitles (`read_formats` lists `sup` or `idx`). */
export function isOcrPage(readFormats: readonly string[] | undefined): boolean {
  return (readFormats ?? []).some((f) => f === 'sup' || f === 'idx');
}

/** True when any dropped file's name says it might be an image subtitle. */
export function mayHoldImageSubtitles(files: readonly File[]): boolean {
  return files.some((f) => mightBeImageSubtitle(f.name));
}

/** What the drop is, reading the first bytes of each file that might be an image subtitle. */
export async function readImageDrop(files: readonly File[]): Promise<ImageDrop<File>> {
  const heads: DroppedHead<File>[] = [];
  for (const file of files) {
    if (!mightBeImageSubtitle(file.name)) continue;
    const head = new Uint8Array(await file.slice(0, 256).arrayBuffer());
    heads.push({ file, name: file.name, head });
  }
  return imageDrop(heads);
}

/** The bytes of a paired drop, as the OCR module takes them. */
export async function sourceOf(
  drop: Extract<ImageDrop<File>, { kind: 'pgs' | 'vobsub' }>,
): Promise<ImageSubtitleSource> {
  if (drop.kind === 'pgs')
    return { kind: 'pgs', sup: new Uint8Array(await drop.sup.arrayBuffer()) };
  return {
    kind: 'vobsub',
    idx: await drop.idx.text(),
    sub: new Uint8Array(await drop.sub.arrayBuffer()),
  };
}

/** The OCR module, imported by URL the first time it is needed. */
export type OcrModule = typeof import('../../../ocr/src/ocr.ts');

let ocrModule: Promise<OcrModule> | null = null;

export function buildId(): string | null {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="build-id"]');
  return meta?.content.trim() || null;
}

export function loadOcr(): Promise<OcrModule> {
  if (ocrModule) return ocrModule;
  const id = buildId();
  const url = id
    ? `/static/build/ocr/ocr.js?v=${encodeURIComponent(id)}`
    : '/static/build/ocr/ocr.js';
  ocrModule = import(
    /* a URL, so esbuild leaves it out of this bundle */ url
  ) as Promise<OcrModule>;
  ocrModule.catch(() => {
    ocrModule = null;
  });
  return ocrModule;
}

/* ── the hand-over from another page ───────────────────────────────────── */

const DB = 'ocr-handover';
const STORE = 'pending';
const KEY = 'next';

interface HandedOver {
  readonly source: ImageSubtitleSource;
  readonly name: string;
  readonly at: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function inStore<T>(
  mode: IDBTransactionMode,
  act: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = act(tx.objectStore(STORE));
        tx.oncomplete = () => {
          db.close();
          resolve(req.result);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      }),
  );
}

/**
 * Send an image subtitle to its OCR page. For the video path: an image track found inside a
 * video, written out as a `.sup` or an `.idx`/`.sub` pair. `name` names the SRT that comes out.
 */
export async function sendImageSubtitlesToOcr(
  source: ImageSubtitleSource,
  name: string,
): Promise<void> {
  const record: HandedOver = { source, name, at: Date.now() };
  await inStore('readwrite', (store) => store.put(record, KEY));
  location.assign(`${OCR_PAGES[source.kind]}#handover`);
}

/** A hand-over older than this is stale (a tab left open, a back button) and is dropped. */
const HANDOVER_TTL_MS = 10 * 60 * 1000;

/**
 * The image subtitle another page sent here, once: it is deleted as it is read. `null` when the
 * page was not opened by a hand-over, or the hand-over is stale.
 */
export async function takeHandedOver(): Promise<{
  source: ImageSubtitleSource;
  name: string;
} | null> {
  if (typeof location === 'undefined' || typeof indexedDB === 'undefined') return null;
  if (location.hash !== '#handover') return null;
  try {
    const record = await inStore<HandedOver | undefined>('readonly', (store) => store.get(KEY));
    await inStore('readwrite', (store) => store.delete(KEY));
    history.replaceState(null, '', location.pathname + location.search);
    if (!record || Date.now() - record.at > HANDOVER_TTL_MS) return null;
    return { source: record.source, name: record.name };
  } catch (err) {
    console.warn('[ocr] the hand-over could not be read', err);
    return null;
  }
}

/* ── the OCR hook: an image track picked from a video (SEO_BRIEF_04 item 7a) ── */

/*
 * A Blu-ray (PGS) or DVD (VobSub) track is pictures of text. The video path takes the track out
 * as the files a dropped one would be (`imageTrack` in the worker, `src/formats/image-track.ts`)
 * and hands them to whatever has registered here. With nothing registered, or a handler that
 * declines, picking one shows B5_IMAGE_TRACK.
 */

export type ImageTrackHandover =
  | {
      readonly kind: 'pgs';
      /** A complete `.sup`, as a disc rip would be. */
      readonly sup: Uint8Array;
      readonly name: string;
      readonly track: ContainerTrackInfo;
      readonly videoName: string;
      readonly warnings: readonly string[];
    }
  | {
      readonly kind: 'vobsub';
      /** The `.idx` text: size, palette, and one timestamp line per picture. */
      readonly idx: string;
      /** The `.sub` MPEG program stream the `.idx` points into. */
      readonly sub: Uint8Array;
      readonly name: string;
      readonly track: ContainerTrackInfo;
      readonly videoName: string;
      readonly warnings: readonly string[];
    };

/**
 * Takes the track and returns once it has taken over the page (shown its own progress and
 * result). Resolve `false` to decline, and the page shows B5_IMAGE_TRACK as if none were set.
 */
export type ImageSubtitleHandler = (handover: ImageTrackHandover) => Promise<boolean>;

let handler: ImageSubtitleHandler | null = null;

/** Set (or with null, clear) the one handler. A second registration replaces the first. */
export function registerImageSubtitleHandler(next: ImageSubtitleHandler | null): void {
  handler = next;
}

export function imageSubtitleHandler(): ImageSubtitleHandler | null {
  return handler;
}

/** The worker's `imageTrack` answer as the handover. Null for a family recognition cannot take. */
export function handoverOf(
  payload: {
    readonly family: 'pgs' | 'vobsub';
    readonly files: readonly { readonly name: string; readonly bytes: Uint8Array }[];
    readonly warnings: readonly string[];
  },
  track: ContainerTrackInfo,
  videoName: string,
): ImageTrackHandover | null {
  const [first, second] = payload.files;
  if (!first) return null;
  if (payload.family === 'pgs') {
    return {
      kind: 'pgs',
      sup: first.bytes,
      name: first.name,
      track,
      videoName,
      warnings: payload.warnings,
    };
  }
  if (!second) return null;
  return {
    kind: 'vobsub',
    idx: new TextDecoder('utf-8').decode(first.bytes),
    sub: second.bytes,
    name: second.name,
    track,
    videoName,
    warnings: payload.warnings,
  };
}

/* ── the two joined: what every tool page registers at boot ─────────────── */

/** The recogniser's input, from the video path's handover (which carries more beside it). */
export function sourceOfHandover(handover: ImageTrackHandover): ImageSubtitleSource {
  return handover.kind === 'pgs'
    ? { kind: 'pgs', sup: handover.sup }
    : { kind: 'vobsub', idx: handover.idx, sub: handover.sub };
}

/**
 * The handler pages/tool.ts registers. `here` recognises on this page, and is passed only on an
 * OCR page (its own panel is there); everywhere else the track goes to its OCR page with
 * `send`. `name` is the extracted file's (`film.fre.sup`), which names the SRT that comes out.
 * A failure (IndexedDB refused, the OCR module did not load) declines, so the page shows
 * B5_IMAGE_TRACK rather than nothing.
 */
export function ocrHandoverHandler(
  here: ((source: ImageSubtitleSource, name: string) => Promise<void>) | null,
  send: (source: ImageSubtitleSource, name: string) => Promise<void> = sendImageSubtitlesToOcr,
): ImageSubtitleHandler {
  return async (handover) => {
    const source = sourceOfHandover(handover);
    try {
      await (here ?? send)(source, handover.name);
      return true;
    } catch (err) {
      console.warn('[ocr] the image track could not be handed over', err);
      return false;
    }
  };
}
